import React, { useEffect, useState } from 'react';

// DEPENDENCIES
import { useFormikContext } from 'formik';
import { Link } from 'react-router-dom';
import moment from 'moment';

// GLOBAL VARIABLES
import { INVENTORY_STATUS, NEW_USED } from 'codes.js';
import { INVENTORY_FILE_TYPE_IDS } from 'globals.js';
import { NUMBER_FORMATS } from 'numberFormats';
import { INTERFACE } from 'pathnames.js';

// GLOBAL FUNCTIONS
import { formatDateTime, makePath } from 'functions.js';

// GLOBAL COMPONENTS
import Card from 'parts/card/Card';
import Form from 'core/form/Form';

// LOCAL VARIABLES
import { INVENTORY_SOURCE_IDS } from 'routers/interface/sections/variables';

// LOCAL COMPONENTS
import Attachment from 'routers/interface/tools/attachment/Attachment';

// STYLE
import styles from './styles.module.scss'

// MAIN COMPONENT
const PurchaseInfoBody = () => {
  const { values } = useFormikContext() || {};
  const {
    inventory_source_method_name,
    date_created,
    purchase_date,
    inventory_source_id,
    inventory_status_name,
    acquired_salesperson_name,
    acquired_salesmanager_name,
    source_sales_id,
    source_name,
    new_used,
    acquired_salesmanager2_name
  } = values;

  const [inventoryCode, setInventoryCode] = useState('S');
  const isUsed = new_used === NEW_USED.used.numeric;
  const isTrade = +inventory_source_id === INVENTORY_SOURCE_IDS.TRADE;

  useEffect(() => {
    const inventory = Object.keys(INVENTORY_STATUS)
      .map(e => INVENTORY_STATUS[e])
      .find(({ label }) => label === inventory_status_name);

    setInventoryCode(inventory ? inventory.code : 'S');
  }, [inventory_status_name]);

  //RENDER
  return (
    <Form.Body>
      <Form.PlainText value={formatDateTime(date_created, 4, true)} label="Date Added" />
      <Form.PlainText name="created_by" label="Added By" />
      <Form.PlainText
        value={formatDateTime(purchase_date, 4, true)}
        label="Purchase Date"
        text="* Must be the date on the Seller's Bill of Sale"
      />
      {(acquired_salesmanager_name || isUsed) && (
        <Form.PlainText
          name="acquired_salesmanager_name"
          label="Acquired By - Sales Manager"
          hidden={['O'].includes(inventoryCode)}
          text={acquired_salesmanager2_name && <span className={styles.text}>{acquired_salesmanager2_name}</span>}
        />
      )}
      {acquired_salesperson_name && (
        <Form.PlainText
          name="acquired_salesperson_name"
          label="Acquired By - Sales Person"
          hidden={['O'].includes(inventoryCode)}
        />
      )}
      <Form.PlainText name="appraised_by" label="Appraised By" hidden={['O'].includes(inventoryCode)} />
      <Attachment.Upload
        label="Trade Appraisal"
        name="documentAttachment.trade_appraisal"
        documentType={INVENTORY_FILE_TYPE_IDS.TRADE_APPRAISAL_USED}
        hidden={!isTrade}
      />
      <Attachment.Upload
        label="Trade Inspection"
        name="documentAttachment.trade_inspection"
        uploadType="inventory"
        documentType={INVENTORY_FILE_TYPE_IDS.TRADE_INSPECTION}
        hidden={!isTrade}
      />
      <Attachment.Upload
        label="Title in Possession"
        name="documentAttachment.trade_title"
        uploadType="inventory"
        documentType={INVENTORY_FILE_TYPE_IDS.TRADE_POSESSION}
        hidden={!isTrade}
      />
      <Form.PlainText name="age_of_vehicle" label="Age" numberFormat={NUMBER_FORMATS.duration.days} />
      <Form.PlainText
        label="Source Method"
        value={`${inventory_source_method_name} : ${moment(date_created).format('MM/DD/YYYY')} at ${moment(
          date_created
        ).format('hh:mm A')}`}
      />
      <Form.PlainText name="inventory_source" label="Source" />
      {source_name && (
        <Form.PlainText
          value={
            source_sales_id ? (
              <Link to={makePath(INTERFACE.sales, 'view', source_sales_id)}>{source_name}</Link>
            ) : (
              source_name
            )
          }
          label="Source Name"
        />
      )}
      <Attachment.Upload
        label="Vehicle Registration"
        name="documentAttachment.trade_registration"
        uploadType="inventory"
        documentType={INVENTORY_FILE_TYPE_IDS.TRADE_REGISTRATION}
        hidden={!isTrade}
      />
    </Form.Body>
  );
};

// EXPORT
export default function PurchaseInfo(props) {
  return <Card title="Purchase Info" body={<PurchaseInfoBody {...props} />} />;
}
